import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { classNames } from "@/utils/classNames";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { ScoreCriteriaType } from "../../schema/score.schema";

import { copySourceCount, CriterionRef, listCriteria } from "./criteriaDraft";

interface CopyScoresMenuProps {
	anchorEl: HTMLElement;
	scoring: ScoreCriteriaType;
	criterionRef: CriterionRef;
	criterionLabel: string;
	others: ScoreCriteriaType[];
	onCopyCriterion: (source: ScoreCriteriaType) => void;
	onCopyAll: (source: ScoreCriteriaType) => void;
	onClose: () => void;
	portalContainer?: HTMLElement | null;
}

const WIDTH = 340;

/**
 * The "copy the scores from a different Lead Scoring" menu (PREC-4055): a
 * scope switch — only the selected criterion, or every criterion — and the
 * other scorings with what each would bring. Portaled and anchored like the
 * matrix menus (web rule §11).
 */
export const CopyScoresMenu: FunctionComponent<CopyScoresMenuProps> = ({
	anchorEl,
	scoring,
	criterionRef,
	criterionLabel,
	others,
	onCopyCriterion,
	onCopyAll,
	onClose,
	portalContainer,
}) => {
	const { t } = useTranslation();
	const menuRef = useRef<HTMLDivElement>(null);
	const [scope, setScope] = useState<"criterion" | "all">("criterion");
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	useLayoutEffect(() => {
		const update = () => {
			const rect = anchorEl.getBoundingClientRect();

			setPosition({
				top: rect.bottom + 4,
				left: Math.max(8, Math.min(rect.left, window.innerWidth - WIDTH - 8)),
			});
		};

		update();
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);

		return () => {
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [anchorEl]);

	useEffect(() => {
		const onMouseDown = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (!menuRef.current?.contains(target) && !anchorEl.contains(target)) {
				onClose();
			}
		};
		const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && onClose();

		document.addEventListener("mousedown", onMouseDown);
		document.addEventListener("keydown", onKeyDown);

		return () => {
			document.removeEventListener("mousedown", onMouseDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [anchorEl, onClose]);

	const countOf = (source: ScoreCriteriaType): string => {
		if (scope === "all") {
			return t("leadScoring.scoringPage.copyCriteria", { count: listCriteria(source).length });
		}

		const count = copySourceCount(scoring, source, criterionRef);

		if (count === null) {
			return t("leadScoring.scoringPage.copyNotPresent");
		}

		return t(
			criterionRef.key === "OTHER"
				? "leadScoring.scoringPage.copyDefinitions"
				: "leadScoring.scoringPage.copyValues",
			{ count }
		);
	};

	const scopeButton = (value: "criterion" | "all", text: string) => (
		<button
			aria-pressed={scope === value}
			className={classNames(
				"h-7 flex-1 cursor-pointer px-2 text-[11px] font-medium",
				scope === value ? "bg-blue-action-200 text-brand-light" : "bg-white text-text-meta"
			)}
			type="button"
			onClick={() => setScope(value)}
		>
			{text}
		</button>
	);

	return createPortal(
		<div
			ref={menuRef}
			className="fixed z-[1600] rounded-lg border border-border-light bg-white p-1.5 shadow-lg"
			data-testid="copy-menu"
			role="menu"
			style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, width: WIDTH }}
		>
			<div className="mb-1.5 flex overflow-hidden rounded-md border border-border-light">
				{scopeButton(
					"criterion",
					t("leadScoring.scoringPage.copyOnly", { criterion: criterionLabel })
				)}
				{scopeButton("all", t("leadScoring.scoringPage.copyAll"))}
			</div>
			<div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-text-disabled">
				{scope === "criterion"
					? t("leadScoring.scoringPage.copyFromTitle", { criterion: criterionLabel })
					: t("leadScoring.scoringPage.copyAllTitle")}
			</div>
			{others.length === 0 ? (
				<div className="px-2 py-1.5 text-xs text-text-meta">
					{t("leadScoring.scoringPage.copyNoOthers")}
				</div>
			) : (
				others.map((source) => (
					<button
						key={source.id}
						className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text hover:bg-background-light"
						data-testid={`copy-from-${source.id}`}
						role="menuitem"
						type="button"
						onClick={() => {
							if (scope === "all") {
								onCopyAll(source);
							} else {
								onCopyCriterion(source);
							}

							onClose();
						}}
					>
						<span className="flex-1 truncate">{source.name}</span>
						<span className="text-[11px] text-text-disabled">{countOf(source)}</span>
					</button>
				))
			)}
		</div>,
		portalContainer ?? document.body
	);
};
